import { Download, LoaderCircle, Pencil, RotateCw, X } from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/Tooltip';
import { cn } from '@shared/components/ui/cn';
import {
  fileExtension,
  fileMetaLabel,
  formatFileSize,
  isArchiveExtension,
  UNKNOWN_FORMAT_LABEL,
} from '@shared/lib/file-format';
import { middleTruncate, splitAtExtension, textMeasurer } from '@shared/lib/middle-truncate';

import { FileFormatTile, type FileFormatTileTone } from './FileFormatTile';

export type FileCardState =
  | 'uploading'
  | 'ready'
  | 'failed'
  | 'unsupported'
  | 'too-large'
  | 'sent'
  | 'preparing'
  | 'result'
  | 'result-failed'
  | 'unavailable';

export interface FileCardProps {
  name: string;
  state: FileCardState;
  sizeBytes?: number;
  progress?: number;
  limitBytes?: number;
  meta?: ReactNode;
  metaTitle?: string;
  onOpen?: () => void;
  openLabel?: string;
  onDownload?: (event: MouseEvent<HTMLElement>) => void;
  downloadHref?: string;
  downloadName?: string;
  onRemove?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  actionsDisabled?: boolean;
  onTryAgain?: () => void;
  showEdit?: boolean;
  onEdit?: () => void;
  thumbnailUrl?: string;
  className?: string;
  openTooltipSide?: 'start' | 'top';
  announceUnavailable?: boolean;
}

const ERROR_STATES: readonly FileCardState[] = [
  'failed',
  'unsupported',
  'too-large',
  'result-failed',
];
const WRAPPING_META_STATES: readonly FileCardState[] = ['result', ...ERROR_STATES];
const REMOVABLE_STATES: readonly FileCardState[] = ['ready', 'failed', 'unsupported', 'too-large'];
const OPENABLE_STATES: readonly FileCardState[] = ['sent', 'result'];

const FOCUSABLE_ACTION = 'button:not([disabled]), a[href]';

type FocusModality = 'keyboard' | 'pointer';

function useMiddleTruncatedName(name: string) {
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [visibleName, setVisibleName] = useState<string | null>(null);

  useLayoutEffect(() => {
    const node = nameRef.current;
    const column = node?.parentElement;
    if (!node || !column) return;
    let active = true;
    const update = () => {
      if (!active) return false;
      const measure = textMeasurer(getComputedStyle(node).font);
      if (!measure) return false;
      const width = column.clientWidth;
      setVisibleName(width > 0 ? middleTruncate(name, width - 1, measure) : null);
      return true;
    };
    if (!update()) return;
    const observer = new ResizeObserver(() => {
      update();
    });
    observer.observe(column);
    const fonts = document.fonts as FontFaceSet | undefined;
    void fonts?.ready.then(update);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [name]);

  return { nameRef, visibleName };
}

function tileTone(state: FileCardState): FileFormatTileTone {
  if (state === 'unsupported') return 'unsupported';
  if (state === 'unavailable') return 'unavailable';
  return 'default';
}

function clampProgress(progress: number | undefined): number {
  if (progress === undefined || !Number.isFinite(progress)) return 0;
  return Math.round(Math.min(100, Math.max(0, progress)));
}

export function FileCard({
  name,
  state,
  sizeBytes,
  progress,
  limitBytes,
  meta,
  metaTitle,
  onOpen,
  openLabel,
  onDownload,
  downloadHref,
  downloadName,
  onRemove,
  onCancel,
  onRetry,
  actionsDisabled = false,
  onTryAgain,
  showEdit = false,
  onEdit,
  thumbnailUrl,
  className,
  openTooltipSide = 'start',
  announceUnavailable = false,
}: FileCardProps) {
  const { t, i18n } = useTranslation('common');
  const metaId = useId();
  const openDescriptionId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const focusModalityRef = useRef<FocusModality | null>(null);
  const focusedByPointerRef = useRef(false);
  const openFocusedRef = useRef(false);
  const { nameRef, visibleName } = useMiddleTruncatedName(name);

  const isOpenable = OPENABLE_STATES.includes(state);
  const isUnavailable = state === 'unavailable';
  const openAction = isOpenable ? onOpen : undefined;
  const removeAction =
    state === 'uploading' ? onCancel : REMOVABLE_STATES.includes(state) ? onRemove : undefined;
  const canOpen = openAction !== undefined;
  const canCancel = state === 'uploading' && onCancel !== undefined;
  const canRetry = state === 'failed' && onRetry !== undefined;
  const canRemove = REMOVABLE_STATES.includes(state) && onRemove !== undefined;
  const canEdit = isOpenable && showEdit && onEdit !== undefined;
  const canDownload = isOpenable && Boolean(downloadHref || onDownload);
  const downloadsByLink = canDownload && Boolean(downloadHref);
  const canTryAgain = state === 'result-failed' && onTryAgain !== undefined;

  useLayoutEffect(() => {
    const card = cardRef.current;
    const active = document.activeElement;
    if (!card || (active && card.contains(active))) return;
    const focusWasOnCardByKeyboard = focusModalityRef.current === 'keyboard';
    const focusWasOnOpen = openFocusedRef.current;
    focusModalityRef.current = null;
    openFocusedRef.current = false;
    const restoresFocus = focusWasOnCardByKeyboard || (focusWasOnOpen && isUnavailable);
    if (!restoresFocus || (active && active !== document.body)) return;
    const target =
      card.tabIndex >= 0
        ? card
        : (card.querySelector<HTMLElement>(FOCUSABLE_ACTION) ??
          (card.hasAttribute('tabindex') ? card : null));
    target?.focus();
  }, [
    state,
    isUnavailable,
    canOpen,
    canCancel,
    canRetry,
    canRemove,
    canEdit,
    canDownload,
    downloadsByLink,
    canTryAgain,
  ]);

  const extension = fileExtension(name);
  const metaLabel = fileMetaLabel(extension);
  const format =
    metaLabel === UNKNOWN_FORMAT_LABEL
      ? t('fileCard.unknownFormat', UNKNOWN_FORMAT_LABEL)
      : metaLabel;
  const size = sizeBytes === undefined ? undefined : formatFileSize(sizeBytes, t, i18n.language);
  const limit = limitBytes === undefined ? undefined : formatFileSize(limitBytes, t, i18n.language);
  const formatAndSize = size ? `${format} · ${size}` : format;
  const cardLabel = size
    ? t('fileCard.cardLabel', '{{name}}, {{format}}, {{size}}', { name, format, size })
    : t('fileCard.cardLabelNoSize', '{{name}}, {{format}}', { name, format });

  const isError = ERROR_STATES.includes(state);
  const hasMetaOverride = meta !== undefined && meta !== null;
  const metaText = typeof meta === 'string' ? meta : undefined;

  function defaultMeta(): string {
    switch (state) {
      case 'failed':
        return t('fileCard.uploadFailed', 'Upload failed — try again');
      case 'unsupported':
        return isArchiveExtension(extension)
          ? t('fileCard.unsupportedArchive', '{{format}} isn’t supported — unzip it', { format })
          : t('fileCard.unsupported', '{{format}} isn’t supported', { format });
      case 'too-large':
        return size && limit
          ? t('fileCard.tooLarge', '{{size}} — the limit is {{limit}}', { size, limit })
          : formatAndSize;
      case 'preparing':
        return t('fileCard.creating', 'Creating the file…');
      case 'result':
        return `${t('fileCard.createdByAi', 'Created by AI CIO')} · ${formatAndSize}`;
      case 'result-failed':
        return t('fileCard.createFailed', 'Couldn’t create the file');
      case 'unavailable':
        return t('fileCard.removedFromVault', 'Removed from Vault');
      default:
        return formatAndSize;
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget || event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    if (!removeAction || actionsDisabled || (event.key !== 'Delete' && event.key !== 'Backspace')) {
      return;
    }
    if (event.key === 'Backspace' && focusedByPointerRef.current) return;
    event.preventDefault();
    removeAction();
  }

  function handlePointerDownCapture() {
    focusModalityRef.current = 'pointer';
    focusedByPointerRef.current = true;
  }

  function handleKeyDownCapture() {
    focusModalityRef.current = 'keyboard';
  }

  function handleFocusCapture(event: FocusEvent<HTMLDivElement>) {
    focusModalityRef.current ??= 'keyboard';
    openFocusedRef.current =
      event.target instanceof HTMLElement && event.target.hasAttribute('data-file-open');
    if (event.target === event.currentTarget) {
      focusedByPointerRef.current = focusModalityRef.current === 'pointer';
    }
  }

  function handleBlurCapture(event: FocusEvent<HTMLDivElement>) {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    focusModalityRef.current = null;
    openFocusedRef.current = false;
  }

  const percent = clampProgress(progress);
  const secondLine =
    state === 'uploading' && !hasMetaOverride ? (
      <div id={metaId} className="flex h-5 w-full items-center gap-2">
        <div
          role="progressbar"
          aria-label={t('fileCard.uploadingNamed', 'Uploading {{name}}', { name })}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-300 dark:bg-neutral-700"
        >
          <div
            className="h-full bg-primary transition-[width]"
            style={{ width: `${String(percent)}%` }}
          />
        </div>
        <span className="shrink-0 text-sm text-muted-foreground">
          {t('fileCard.progress', '{{value}}%', { value: percent })}
        </span>
      </div>
    ) : (
      <div
        id={metaId}
        title={
          metaTitle === undefined
            ? (metaText ?? (isError && !hasMetaOverride ? defaultMeta() : undefined))
            : `\u2068${metaTitle}\u2069`
        }
        dir={metaText === undefined ? undefined : 'auto'}
        className={cn(
          WRAPPING_META_STATES.includes(state) ? 'line-clamp-2 break-words' : 'truncate',
          metaText !== undefined && 'w-fit max-w-full',
          'text-sm',
          isError ? 'text-destructive dark:text-red-400' : 'text-muted-foreground',
        )}
      >
        {hasMetaOverride ? meta : defaultMeta()}
      </div>
    );
  const announcement = isError
    ? (metaText ?? defaultMeta())
    : isUnavailable && announceUnavailable
      ? t('fileCard.removedFromVault', 'Removed from Vault')
      : '';

  const cancelButton = canCancel ? (
    <CardAction
      label={t('fileCard.cancelUploadNamed', 'Cancel uploading {{name}}', { name })}
      tooltip={t('fileCard.cancelUpload', 'Cancel upload')}
      disabled={actionsDisabled}
      onClick={onCancel}
    >
      <X aria-hidden="true" />
    </CardAction>
  ) : null;
  const retryButton = canRetry ? (
    <CardAction
      label={t('fileCard.retryNamed', 'Retry uploading {{name}}', { name })}
      tooltip={t('fileCard.retry', 'Retry')}
      disabled={actionsDisabled}
      onClick={onRetry}
    >
      <RotateCw aria-hidden="true" />
    </CardAction>
  ) : null;
  const removeButton = canRemove ? (
    <CardAction
      label={t('fileCard.removeNamed', 'Remove {{name}}', { name })}
      tooltip={t('fileCard.remove', 'Remove')}
      disabled={actionsDisabled}
      onClick={onRemove}
    >
      <X aria-hidden="true" />
    </CardAction>
  ) : null;
  const editButton = canEdit ? (
    <CardAction
      label={t('fileCard.edit', 'Edit in Investbanq')}
      tooltip={t('fileCard.edit', 'Edit in Investbanq')}
      onClick={onEdit}
    >
      <Pencil aria-hidden="true" />
    </CardAction>
  ) : null;
  const downloadButton = canDownload ? (
    <CardAction
      label={t('fileCard.downloadNamed', 'Download {{name}}', { name })}
      tooltip={t('fileCard.download', 'Download')}
      href={downloadHref}
      downloadName={downloadName}
      onClick={onDownload}
    >
      <Download aria-hidden="true" />
    </CardAction>
  ) : null;
  const tryAgainButton = canTryAgain ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="rounded-lg"
      aria-label={t('fileCard.tryAgainNamed', 'Try again to create {{name}}', { name })}
      onClick={onTryAgain}
    >
      {t('fileCard.tryAgain', 'Try again')}
    </Button>
  ) : null;
  const preparingSpinner =
    state === 'preparing' ? (
      <div className="grid size-8 place-items-center">
        <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
      </div>
    ) : null;
  const emptySlot = isUnavailable ? <div className="size-8" /> : null;
  const hasActions = [
    cancelButton,
    retryButton,
    removeButton,
    editButton,
    downloadButton,
    tryAgainButton,
    preparingSpinner,
    emptySlot,
  ].some((action) => action !== null);

  const { head, tail } = splitAtExtension(name);
  const isNameTruncated = visibleName !== null && visibleName !== name;
  const openTooltip = openLabel ?? t('fileCard.open', 'Open in Vault');
  const isCardGroup = Boolean(removeAction) || isUnavailable;

  return (
    <TooltipProvider>
      <div
        ref={cardRef}
        role={isCardGroup ? 'group' : undefined}
        tabIndex={removeAction ? 0 : isUnavailable ? -1 : undefined}
        aria-label={isCardGroup ? cardLabel : undefined}
        aria-describedby={isCardGroup ? metaId : undefined}
        onKeyDown={removeAction ? handleKeyDown : undefined}
        onKeyDownCapture={handleKeyDownCapture}
        onPointerDownCapture={handlePointerDownCapture}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
        className={cn(
          'relative isolate flex w-full items-center gap-3 rounded-lg border bg-background py-3 ps-3 pe-2',
          isError && 'border-[#fecaca] bg-red-50 dark:border-red-900/60 dark:bg-red-950/40',
          isUnavailable && 'border-dashed bg-neutral-50 dark:bg-neutral-900',
          openAction && 'hover:bg-accent',
          isCardGroup &&
            'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset',
          className,
        )}
      >
        {openAction && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={cardLabel}
                aria-describedby={openDescriptionId}
                data-file-open=""
                onClick={openAction}
                className="absolute inset-0 cursor-pointer rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset"
              />
            </TooltipTrigger>
            <TooltipContent
              side={openTooltipSide === 'top' ? 'top' : i18n.dir() === 'rtl' ? 'right' : 'left'}
              sideOffset={4}
            >
              {isNameTruncated ? (
                <>
                  <span dir="auto" className="block break-all">
                    {name}
                  </span>
                  <span className="block">{openTooltip}</span>
                </>
              ) : (
                openTooltip
              )}
            </TooltipContent>
          </Tooltip>
        )}
        {openAction && (
          <span id={openDescriptionId} hidden>
            {openTooltip}
          </span>
        )}
        {thumbnailUrl && !isUnavailable ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="size-10 shrink-0 rounded-lg border object-cover"
          />
        ) : (
          <FileFormatTile extension={extension} tone={tileTone(state)} />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p
            ref={nameRef}
            dir="auto"
            title={openAction ? undefined : name}
            className={cn(
              'flex w-fit max-w-full overflow-hidden text-sm font-medium whitespace-nowrap text-foreground',
              isUnavailable && 'opacity-60',
            )}
          >
            {isNameTruncated ? (
              <>
                <span className="sr-only">{name}</span>
                <span aria-hidden="true" className="select-none">
                  {visibleName}
                </span>
              </>
            ) : visibleName === null ? (
              <>
                <span className="truncate">{head}</span>
                {tail && <span className="shrink-0">{tail}</span>}
              </>
            ) : (
              name
            )}
          </p>
          {secondLine}
        </div>
        <span role="status" aria-live="polite" className="sr-only">
          {announcement}
        </span>
        {hasActions && (
          <div className="relative z-10 flex shrink-0 items-center">
            {cancelButton}
            {retryButton}
            {removeButton}
            {editButton}
            {downloadButton}
            {tryAgainButton}
            {preparingSpinner}
            {emptySlot}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

interface CardActionProps {
  label: string;
  tooltip: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  href?: string;
  downloadName?: string;
  disabled?: boolean;
  children: ReactNode;
}

function CardAction({
  label,
  tooltip,
  onClick,
  href,
  downloadName,
  disabled,
  children,
}: CardActionProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {href ? (
          <Button asChild variant="ghost" size="icon-sm" className="rounded-lg">
            <a
              href={href}
              download={downloadName}
              target={downloadName === undefined ? '_blank' : undefined}
              rel="noreferrer"
              aria-label={label}
              onClick={onClick}
            >
              {children}
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="rounded-lg"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
          >
            {children}
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent sideOffset={4}>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
